
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useAuth } from "./useAuth"
import { errorHandler } from "@/utils/errorHandler"
import { logger } from "@/utils/logger"

export const useCreateDirectConversation = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: {
      participant_user_id: string
      title?: string
    }) => {
      if (!profile?.id) {
        const error = new Error("Usuário não autenticado")
        logger.error("Tentativa de criação sem autenticação", "useCreateDirectConversation")
        errorHandler.handleError({
          code: 'PERMISSION_DENIED',
          message: error.message
        })
        throw error
      }

      try {
        logger.info("Criando conversa direta", "useCreateDirectConversation", {
          participantId: data.participant_user_id,
          userId: profile.id
        })

        // Verificar se já existe uma conversa entre os dois usuários
        const { data: existingConversations, error: searchError } = await supabase
          .from('conversations')
          .select(`
            id,
            conversation_participants!inner(user_id)
          `)
          .eq('conversation_type', 'direct')
          .eq('is_active', true)

        if (searchError) {
          throw searchError
        }

        // Filtrar conversas onde ambos os usuários participam
        const existingConversation = existingConversations?.find(conv => {
          const participantIds = conv.conversation_participants.map(p => p.user_id)
          return participantIds.includes(profile.id) && participantIds.includes(data.participant_user_id) && participantIds.length === 2
        })

        if (existingConversation) {
          logger.info("Conversa existente encontrada", "useCreateDirectConversation", { 
            conversationId: existingConversation.id 
          })
          return existingConversation.id
        }

        // Criar nova conversa (sem título fixo para deixar a lógica determinar baseado nos participantes)
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            created_by: profile.id,
            conversation_type: 'direct',
            is_group: false
          })
          .select()
          .single()

        if (createError) {
          throw createError
        }

        // Adicionar participantes
        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert([
            {
              conversation_id: newConversation.id,
              user_id: profile.id
            },
            {
              conversation_id: newConversation.id,
              user_id: data.participant_user_id
            }
          ])

        if (participantsError) {
          throw participantsError
        }

        logger.info("Conversa criada com sucesso", "useCreateDirectConversation", { 
          conversationId: newConversation.id 
        })
        
        return newConversation.id
      } catch (error) {
        logger.error("Erro na criação da conversa", "useCreateDirectConversation", error)
        throw error
      }
    },
    onSuccess: (conversationId) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] })
      logger.info("Conversa criada - queries invalidadas", "useCreateDirectConversation")
      toast.success("Conversa criada com sucesso!")
    },
    onError: (error: any) => {
      logger.error("Erro no callback de criação", "useCreateDirectConversation", error)
    }
  })
}
